// Slate Coder: a first-party plugin that brings code tooling to Slate. Today it
// contributes code folding for the plain-text editor (by markdown headings, by
// code symbols, and an indentation fallback); more coding features will follow.
//
// Like the Hello example it runs trusted in-process, but it registers its fold
// providers through the PUBLIC slate.editor.registerFoldProvider API so it
// exercises the same contribution path a third-party plugin would.

import {
  MARKDOWN_LANGUAGE,
  SUPPORTED_OUTLINE_EXTENSIONS,
  markdownHeadingFolds,
  indentFolds,
  foldsFromSymbols,
  outlineLanguageForFile,
  type FoldRange,
} from '@slate/core';
import type { ActivationContext, PluginManifest, PluginModule } from '@slate/plugin-api';
import { extractSymbolsInWorker } from '../perf/symbolsClient';

export const SLATE_CODER_PLUGIN_ID = 'slate.coder';

/**
 * Extensions the indentation fallback folds: common indented languages NOT
 * already covered by the markdown-heading or code-symbol providers, plus plain
 * text. Kept disjoint from those so a file is folded by exactly one strategy.
 */
const INDENT_FALLBACK_EXTENSIONS: readonly string[] = [
  'txt',
  'py',
  'pyi',
  'rb',
  'go',
  'rs',
  'java',
  'kt',
  'kts',
  'php',
  'c',
  'cc',
  'cpp',
  'cxx',
  'h',
  'hpp',
  'cs',
  'swift',
  'scala',
  'lua',
  'r',
  'dart',
  'sql',
  'sh',
  'bash',
  'zsh',
  'fish',
  'yaml',
  'yml',
  'toml',
  'ini',
  'json',
  'jsonc',
  'css',
  'scss',
  'sass',
  'less',
  'styl',
  'html',
  'xml',
  'vue',
  'svelte',
  'graphql',
  'gql',
];

/**
 * Register Slate Coder's fold providers. Folding lives in its own helper so
 * future coding features slot in alongside it without touching `activate`.
 */
function registerFolding(ctx: ActivationContext): void {
  const { slate } = ctx;

  // (a) Markdown: fold each heading section to the line before the next heading
  //     of shallower-or-equal depth (or EOF).
  ctx.subscriptions.push(
    slate.editor.registerFoldProvider({
      id: 'markdown',
      extensions: MARKDOWN_LANGUAGE.extensions,
      getFolds: (source) => markdownHeadingFolds(source),
    }),
  );

  // (b) Code: fold symbol spans (classes, functions, methods) for outline-
  //     supported files, reusing the existing off-thread symbol extraction.
  ctx.subscriptions.push(
    slate.editor.registerFoldProvider({
      id: 'code',
      extensions: [...SUPPORTED_OUTLINE_EXTENSIONS],
      getFolds: async (source, fileName): Promise<FoldRange[]> => {
        const lang = outlineLanguageForFile(fileName);
        if (!lang) return [];
        const symbols = await extractSymbolsInWorker(source, lang);
        return foldsFromSymbols(symbols);
      },
    }),
  );

  // (c) Indentation fallback for everything else.
  ctx.subscriptions.push(
    slate.editor.registerFoldProvider({
      id: 'indent',
      extensions: INDENT_FALLBACK_EXTENSIONS,
      getFolds: (source) => indentFolds(source),
    }),
  );
}

export const slateCoderModule: PluginModule = {
  activate(ctx) {
    registerFolding(ctx);
  },
};

export const SLATE_CODER_README: string = [
  '# Slate Coder',
  '',
  'Code tools for Slate. The first feature is code folding for the plain-text',
  'editor; more coding features are on the way.',
  '',
  '## Folding',
  '',
  'Fold and unfold regions from the editor fold gutter (or with the standard fold',
  'keybindings). Slate Coder works out what is foldable three ways:',
  '',
  '- **Markdown** folds each heading down to the next heading of the same or a',
  '  higher level.',
  '- **Code** folds classes, functions, and methods in supported source files,',
  '  using the same symbol analysis that powers the outline.',
  '- **Indentation** is a conservative fallback for everything else: a line folds',
  '  to the last following line indented more deeply.',
  '',
  'Disable Slate Coder any time to turn folding off; re-enable it to bring it back.',
].join('\n');

export const SLATE_CODER_MANIFEST: PluginManifest = {
  id: SLATE_CODER_PLUGIN_ID,
  name: 'Slate Coder',
  version: '1.0.0',
  publisher: 'Slate',
  description:
    'Code tools for Slate. Adds code folding by headings, functions, and indentation, with more coding features to come.',
  engines: { slate: '^1.0.0' },
  readme: SLATE_CODER_README,
  contributes: {
    foldProviders: [{ language: 'markdown' }, { language: 'typescript' }, { language: 'javascript' }],
  },
};
